import { Alert, AlertTitle, Button, Collapse } from '@mui/material';
import {
  GoogleAuthProvider,
  getAuth,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence, // <<< NOVO IMPORT
  browserLocalPersistence, // <<< NOVO IMPORT
} from 'firebase/auth';
import { useSnackbar } from 'notistack';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { firebaseApp } from '../config/firebaseConfig';
import {
  getDataUser,
  getToken,
  saveDataUser,
  saveToken,
  removeAccess,
} from '../services/auth/access-service';
import { useWSContext } from './WebSocketContext';
import { getUserTipo } from '../services/getUserTipo';
import { updateUser, updateUserPassword } from '../services/userservice';
import { verificarAdmin } from '../services/adminService'; // 👈 Novo import

const googleProvider = new GoogleAuthProvider();
export const AuthContext = createContext(null);

const HANDLERS = {
  INITIALIZE: 'INITIALIZE',
  SIGN_IN: 'SIGN_IN',
  SIGN_OUT: 'SIGN_OUT',
};

const handlers = {
  [HANDLERS.INITIALIZE]: (state, action) => {
    const user = action.payload;
    const token = getToken();
    return {
      ...state,
      ...(user && token
        ? { isAuthenticated: true, isLoading: false, user, cliente: user }
        : { isLoading: false }),
    };
  },
  [HANDLERS.SIGN_IN]: (state, action) => {
    const user = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
      cliente: user,
    };
  },
  [HANDLERS.SIGN_OUT]: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    cliente: null,
  }),
};

const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  cliente: null,
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export function AuthProvider(props) {
  const { disconnect } = useWSContext();
  const { children } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [openAlert, setOpenAlert] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const initialized = useRef(false);
  const { login, logout: logoutWS } = useWSContext();

  const initialize = useCallback(async () => {
    const logoutFlag = sessionStorage.getItem('logoutInProgress');
    if (logoutFlag) {
      sessionStorage.removeItem('logoutInProgress');
      dispatch({ type: HANDLERS.INITIALIZE });
      return;
    }

    if (initialized.current) return;
    initialized.current = true;

    const token = getToken();
    if (token) {
      const user = getDataUser();
      login(token);
      dispatch({ type: HANDLERS.INITIALIZE, payload: user });
    } else {
      dispatch({ type: HANDLERS.INITIALIZE });
    }
  }, [login]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const closeAlert = () => setOpenAlert(false);

  const resetPassword = async (email) => {
    const auth = getAuth(firebaseApp);
    try {
      await sendPasswordResetEmail(auth, email);
      enqueueSnackbar('Link de redefinição enviado! Verifique seu e-mail.', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Erro ao enviar e-mail de redefinição:', error.code);
      if (error.code === 'auth/user-not-found') {
        enqueueSnackbar('Nenhum usuário encontrado com este e-mail.', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ocorreu um erro. Tente novamente.', { variant: 'error' });
      }
      throw error;
    }
  };

  const updateUserProfile = useCallback(
    async (updatedUserInfo) => {
      try {
        // LOG PARA VERIFICAR COMO ESTÁ INDO AS EDIÇÕES DO PERFIL DO USUARIO
        // console.log('updateUserProfile - updatedUserInfo:', updatedUserInfo);

        await updateUser(updatedUserInfo.id, updatedUserInfo);
        dispatch({
          type: HANDLERS.SIGN_IN,
          payload: {
            ...state.cliente,
            ...updatedUserInfo,
          },
        });
        saveDataUser({
          ...state.cliente,
          ...updatedUserInfo,
        });
        enqueueSnackbar('Perfil atualizado com sucesso!', { variant: 'success' });
      } catch (error) {
        console.error('Erro ao atualizar perfil:', error);
        enqueueSnackbar(`Erro ao atualizar perfil: ${error.message}`, {
          variant: 'error',
        });
        throw error;
      }
    },
    [dispatch, enqueueSnackbar, state.cliente]
  );

  const updateUserPasswordFunc = useCallback(
    async (newPassword) => {
      try {
        const userInfo = state.cliente;
        // console.log('userInfo:', userInfo);
        // console.log('userInfo.id:', userInfo?.id);
        if (!userInfo || !userInfo.id) {
          throw new Error('Usuário não autenticado ou ID não encontrado');
        }
        await updateUserPassword(userInfo.id, userInfo, newPassword);
        enqueueSnackbar('Senha atualizada com sucesso!', { variant: 'success' });
      } catch (error) {
        console.error('Erro ao atualizar senha:', error);
        enqueueSnackbar(`Erro ao atualizar senha: ${error.message}`, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, state.cliente]
  );

  const createUserDataSession = useCallback(
    async (userRes) => {
      window.localStorage.setItem('authenticated', 'true');
      const userFirestore = await getUserTipo();
      const isAdmin = await verificarAdmin(userRes.uid); // 👈 Usa o import

      const user = {
        id: userRes.uid,
        avatar: userRes.photoURL,
        email: userRes.email,
        nome: userRes.displayName || userFirestore.nome,
        tipo: userFirestore.tipo,
        auth_only: userFirestore.auth_only,
        ultimoLogin: userRes.metadata?.lastSignInTime,
        isAdmin,
      };

      const token = await userRes.getIdToken();
      if (token) {
        login(token);
        saveToken(token);
      }

      saveDataUser(user);
      dispatch({ type: HANDLERS.SIGN_IN, payload: user });

      const displayName = user.nome ?? user.email;
      enqueueSnackbar(`Seja bem-vindo, ${displayName}!`, {
        variant: 'success',
      });
    },
    [dispatch, enqueueSnackbar, login]
  );

  const logout = useCallback(async () => {
    try {
      const auth = getAuth(firebaseApp);
      await signOut(auth);
      logoutWS?.();
      localStorage.clear();
      sessionStorage.clear();
      removeAccess();
      dispatch({ type: HANDLERS.SIGN_OUT });
      navigate('/app/home');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      enqueueSnackbar('Erro ao sair. Tente novamente.', { variant: 'error' });
    }
  }, [logoutWS, enqueueSnackbar, navigate]);

  const signInWithEmailPassword = async (email, password) => {
    const auth = getAuth(firebaseApp);
    try {
      // <<< MUDANÇA AQUI!
      // Define a persistência ANTES de tentar fazer o login.
      await setPersistence(auth, browserLocalPersistence);

      const result = await signInWithEmailAndPassword(auth, email, password);
      await result.user.reload();
      const { currentUser } = auth;

      if (currentUser && currentUser.emailVerified) {
        await createUserDataSession(currentUser);
      } else {
        await sendEmailVerification(currentUser);
        await signOut(auth);
        enqueueSnackbar('Seu e-mail ainda não foi verificado!', {
          variant: 'warning',
          persist: true,
        });
        enqueueSnackbar(
          'Enviamos um novo link de verificação para sua caixa de entrada.',
          {
            variant: 'info',
            persist: true,
          }
        );
        throw new Error('EMAIL_NOT_VERIFIED');
      }
    } catch (error) {
      if (
        error.code === 'auth/user-not-found' ||
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/invalid-credential'
      ) {
        enqueueSnackbar('Usuário ou senha inválido!', { variant: 'error' });
      } else if (error.message !== 'EMAIL_NOT_VERIFIED') {
        enqueueSnackbar('Ocorreu um erro ao tentar fazer login.', {
          variant: 'error',
        });
      }
      throw error;
    }
  };

  const signInWithGoogle = useCallback(async () => {
    if (isLoggingIn) return;
    const auth = getAuth(firebaseApp);
    googleProvider.setCustomParameters({});
    setIsLoggingIn(true);
    try {
      // <<< MUDANÇA AQUI!
      // Define a persistência ANTES de tentar fazer o login com Google.
      await setPersistence(auth, browserLocalPersistence);

      const result = await signInWithPopup(auth, googleProvider);
      await result.user.reload();
      const { currentUser } = auth;

      if (currentUser && currentUser.emailVerified) {
        await createUserDataSession(currentUser);
      } else {
        await sendEmailVerification(currentUser);
        await signOut(auth);
        enqueueSnackbar('Seu e-mail ainda não foi verificado em nossa plataforma!', {
          variant: 'warning',
          persist: true,
        });
        enqueueSnackbar('Enviamos um novo link de verificação para o seu e-mail.', {
          variant: 'info',
          persist: true,
        });
        throw new Error('EMAIL_NOT_VERIFIED');
      }
    } catch (error) {
      if (error.message !== 'EMAIL_NOT_VERIFIED') {
        console.error('Erro no login com Google:', error);
        enqueueSnackbar('Não foi possível fazer login com o Google.', {
          variant: 'error',
        });
      }
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  }, [enqueueSnackbar, createUserDataSession, isLoggingIn]);

  const value = useMemo(
    () => ({
      ...state,
      signInWithGoogle,
      signInWithEmailPassword,
      logout,
      resetPassword,
      updateUserProfile,
      updateUserPassword: updateUserPasswordFunc,
    }),
    [
      state,
      signInWithGoogle,
      logout,
      resetPassword,
      updateUserProfile,
      updateUserPasswordFunc,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      <Collapse in={openAlert}>
        <Alert
          variant="standard"
          severity="warning"
          action={
            <Button variant="outlined" color="inherit" onClick={closeAlert}>
              OK
            </Button>
          }
        >
          <AlertTitle>Verificação de e-mail</AlertTitle>
          Foi enviado no seu email um link de verificação de email. Clique no link
          recebido e tente logar novamente!
        </Alert>
      </Collapse>
      {children}
    </AuthContext.Provider>
  );
}

export const AuthConsumer = AuthContext.Consumer;

export const useAuthContext = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    const error = new Error(
      'useAuthContext deve ser usado dentro de um <AuthProvider>'
    );
    console.error(error.stack);
    throw error;
  }
  return authContext;
};
